// The Candidates screen (`#candidates`, DS DC-91 / candidate C-14): the candidate master as a security-trimmed table —
// "The Candidates screen is a security-trimmed table with saved views, not a Kanban; tables do not lift." Each row is
// what candidate.search lets this seat see: full for the owner and the seated, the token for the HR Business Partner,
// a masked row for a profile outside the viewer's scope. Add candidate is drawn only when candidate.create is callable
// (Matrix *None* → the control is absent, DC-24); it opens from the URL (`?action=add`). Saved views and further
// columns are slots another lane fills (slots.ts, C-B10-13): B10 registers its saved views in the header's slot, where
// surface `candidates` keeps the status and internal/external filters, never the search (Law 9).
// The read (UAT F-59): the table draws its skeleton rows for as long as the read for the current filters has no answer
// (pending, whether or not it is fetching yet), and the read is keyed by the filters themselves — blanks dropped, words
// trimmed, typing settled — so it fires once per filter, never once per keystroke.
// The list (UAT D-12; CA-1, CA-10, CA-12): the table sits on the flush card Talent community uses; it pages, the stepper
// reading the rows shown and the total the search counts ("1–50 of 312", DS #70's *1 of n*); it sorts by Name or by his
// Last contact (DS §3.4: V1's *Last activity* on C-14 becomes **Last contact**); it carries Current employer and Last
// contact from the search row; and every filter candidate.search takes is drawn under Filters, each chosen one a chip in
// the bar. Save view and Export are B10's. A masked row stays the Candidate ID alone, so it shows neither column.
// The HR Business Partner's token row (UAT F-21): no name, so the row reads the token label (the word Candidate and the
// token's last four characters, the convention of views/Candidates.tsx) and opens the requisition application that seat
// may read; every value the seat does not see reads his masked words, *subject to permission*, never a blank.
// Hosted by MissionShell: views/b3/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { CandidateSearchInput, SearchRow } from "@/api/tools";
import { candidateSearch } from "@/api/tools";
import { go, useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import {
  ACCESS_LABELS, Banner, Button, Card, COMMON_LABELS, Count, DIALOG_LABELS, Disclosure, DOCUMENT_LABELS, ErrorStrip, Field, FilterBar, Flex,
  G7_TEMPLATES, Grid, Icon, IconButton, IdPill, Input, MASKED_LABELS, MaskedRow, NAV_ITEM_LABELS, PageHeader, Pill, RULE_LABELS, Select,
  Stack, Table, Td, TextLink, Th, Toggle, Tr, type FilterBarFilter,
} from "@/mc";
import { AddCandidateDialog } from "./AddCandidateDialog";
import { BRD } from "./brd";
import { usePeopleChoices, useSourceChoices, useSourceWord, wordIn } from "./options";
import { queryOf, useSettledRead, withQuery } from "@/views/shared/settledRead";
import { CANDIDATE_LABELS as L, PROFILE_LABELS as P } from "./labels";
import { REASONS_ADDED, reasonWord, TAG, tagSlug } from "./profile";
import { TaxonomySelect, useTaxonomy } from "./ProfileDialogs";
import { refusalSentence } from "./refusals";
import { candidateColumns, candidateSavedViews, type CandidateColumnDef } from "./slots";

const STATUS_WORD: Readonly<Record<SearchRow["status"], string>> = { Active: L.active, Archived: L.archived, Merged: L.merged };
const COLUMNS = 10;

/** The list's filters as the screen holds them — every filter candidate.search takes; blank is no filter. Tags are
 *  typed comma separated, the prior requisition by its id, Last contact as the day from which members were contacted. */
export interface CandidateFilters {
  q: string;
  status: "" | SearchRow["status"];
  internal: "" | "internal" | "external";
  owner: string;
  source: string;
  community: boolean;
  reason: string;
  segment: string;
  tags: string;
  prior: string;
  lastContact: string;
}

export const NO_CANDIDATE_FILTERS: CandidateFilters = {
  q: "", status: "", internal: "", owner: "", source: "", community: false, reason: "", segment: "", tags: "", prior: "", lastContact: "",
};

/** Name, or his Last contact: the members by their last contact, newest first, then everyone else by name. */
export type CandidateSort = "name" | "last_contact";

/** Rows on a page. */
export const PAGE_SIZE = 50;

type Reason = NonNullable<CandidateSearchInput["reason_added"]>[number];

/** The search one filter state, sort and page ask: blanks dropped and words trimmed, so the same list is always the
 *  same read. */
export function candidateSearchInput(filters: CandidateFilters, sort: CandidateSort = "name", page = 0): CandidateSearchInput {
  const input: CandidateSearchInput = { limit: PAGE_SIZE };
  const q = filters.q.trim();
  if (q) input.q = q;
  if (filters.status) input.status = filters.status;
  if (filters.internal) input.internal = filters.internal === "internal";
  if (filters.owner) input.owner_id = filters.owner;
  if (filters.source) input.source = filters.source;
  if (filters.community) input.community_only = true;
  if (filters.reason) input.reason_added = [filters.reason as Reason];
  if (filters.segment) input.segment = filters.segment;
  const tags = filters.tags.split(",").map(tagSlug).filter((tag) => TAG.test(tag));
  if (tags.length > 0) input.tags = tags;
  const prior = filters.prior.trim().toUpperCase();
  if (prior) input.prior_requisition_id = prior;
  if (/^\d{4}-\d{2}-\d{2}$/.test(filters.lastContact)) input.last_contact_after = `${filters.lastContact}T00:00:00Z`;
  if (sort !== "name") input.sort = sort;
  if (page > 0) input.offset = page * PAGE_SIZE;
  return input;
}

/** The read's key: one per filter state, sort and page (UAT F-59). */
export const candidateSearchKey = (input: CandidateSearchInput): readonly unknown[] => ["candidate-search", "list", input];

/** The rows a page shows over the total, as his stepper reads them: "1–50 of 312". */
export function pageRange(page: number, total: number): string {
  return G7_TEMPLATES.pageOf({ n: `${page * PAGE_SIZE + 1}–${Math.min(total, (page + 1) * PAGE_SIZE)}`, total });
}

/** How long typing pauses before the words are searched. */
export const SEARCH_PAUSE_MS = 300;

/** Settles what is typed: `onSettled` receives the last value once `ms` pass without another; `cancel` drops a pending one. */
export function settler<T>(onSettled: (value: T) => void, ms: number = SEARCH_PAUSE_MS): ((value: T) => void) & { cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  return Object.assign((value: T) => {
    cancel();
    timer = setTimeout(() => {
      timer = null;
      onSettled(value);
    }, ms);
  }, { cancel });
}

/** The masked-candidate token label, as views/Candidates.tsx writes it: the word Candidate and the token's last four. */
const tokenLabel = (token: string) => `Candidate ${token.slice(-4).toUpperCase()}`;

/** The application a row opens: the first one the reader is seated on (the server sends its id only there). */
function openedApplication(row: SearchRow): string | null {
  const app = (row.applications ?? []).find((fact) => fact.application_id);
  return app?.application_id ? `#requisitions/${app.requisition_id}/candidates/${app.application_id}` : null;
}

export function CandidatesPage() {
  return <CandidatesBody />;
}

function CandidatesBody() {
  const route = useRoute();
  const { can } = useSession();
  const flags = queryOf(route.raw);
  const adding = can("candidate.create") && flags.get("action") === "add";
  return (
    <CandidatesList
      onAdd={() => go(withQuery("#candidates", { action: "add" }, flags))}
      dialog={adding ? (
        <AddCandidateDialog
          flags={flags}
          onClose={() => go(withQuery("#candidates", { action: null, method: null }, flags))}
          onSaved={(result) => go(`#candidates/${result.candidateId}`)}
        />
      ) : null}
    />
  );
}

/** The list itself — its filters, its read and its table; the route's parts (Add candidate's door and dialog) come in. */
export function CandidatesList({ onAdd, dialog }: { onAdd: () => void; dialog: ReactNode }) {
  const { can } = useSession();
  const { register } = useViewState();
  // what the controls show (draft) and what is searched (filters): a choice applies at once, typing once it settles
  const [draft, setDraft] = useState<CandidateFilters>(NO_CANDIDATE_FILTERS);
  const [filters, setFilters] = useState<CandidateFilters>(NO_CANDIDATE_FILTERS);
  const [sort, setSort] = useState<CandidateSort>("name");
  const [page, setPage] = useState(0);
  const settle = useMemo(() => settler<CandidateFilters>((next) => {
    setFilters(next);
    setPage(0);
  }), []);
  useEffect(() => settle.cancel, [settle]);
  const typeIn = (key: "q" | "tags" | "prior", text: string) => {
    const next = { ...draft, [key]: text };
    setDraft(next);
    settle(next);
  };
  const choose = (patch: Partial<CandidateFilters>) => {
    settle.cancel();
    const next = { ...draft, ...patch };
    setDraft(next);
    setFilters(next);
    setPage(0);
  };
  const input = candidateSearchInput(filters, sort, page);
  const list = useSettledRead(candidateSearchKey(input), () => candidateSearch(input));
  // pending: no answer yet for these filters, fetching or not (UAT F-59: never the bare head row)
  const pending = list.isPending;
  // the owner filter's Recruiters, where this seat may list them (people.options); otherwise the filter is not drawn
  const mayPickOwner = can("people.options");
  const owners = usePeopleChoices("recruiter", mayPickOwner);
  const sources = useSourceChoices();
  const segment = useTaxonomy("segment");
  // C-B10-13: the columns and the saved-views control other lanes register (slots.ts); a saved view is the search and
  // filters as flags, applied to this table's own state
  const extraColumns = candidateColumns.list().filter((column) => column.visible?.(can) ?? true);
  const SavedViews = candidateSavedViews.list()[0]?.component;
  const viewQuery = new URLSearchParams([
    ...(filters.q.trim() ? [["q", filters.q.trim()]] : []), ...(filters.status ? [["status", filters.status]] : []), ...(filters.internal ? [["internal", filters.internal]] : []),
  ]);
  const statusOf = (value: string | null) => (["Active", "Archived", "Merged"] as const).find((status) => status === value) ?? "";
  const internalOf = (value: string | null) => (value === "internal" || value === "external" ? value : "");
  const applyView = (next: string) => {
    const saved = new URLSearchParams(next);
    choose({ q: saved.get("q") ?? "", status: statusOf(saved.get("status")), internal: internalOf(saved.get("internal")) });
  };
  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  useEffect(() => register({ view: "candidates", ids: {}, summary: { loading: pending, count: rows.length } }), [register, pending, rows.length]);

  const mayAdd = can("candidate.create");
  const chips: (FilterBarFilter | null)[] = [
    filters.status ? { name: L.status, value: STATUS_WORD[filters.status], onRemove: () => choose({ status: "" }), hs: "cand.filter.status" } : null,
    filters.internal ? { name: L.internalExternal, value: filters.internal === "internal" ? L.internal : L.external, onRemove: () => choose({ internal: "" }), hs: "cand.filter.internal" } : null,
    filters.owner ? { name: L.owner, value: wordIn(owners.choices, filters.owner) ?? filters.owner, onRemove: () => choose({ owner: "" }), hs: "cand.filter.owner" } : null,
    filters.source ? { name: L.source, value: wordIn(sources, filters.source) ?? filters.source, onRemove: () => choose({ source: "" }), hs: "cand.filter.source" } : null,
    filters.community ? { name: L.talentCommunity, value: ACCESS_LABELS.Yes, onRemove: () => choose({ community: false }), hs: "cand.filter.community" } : null,
    filters.reason ? { name: P.reasonDateAdded, value: reasonWord(filters.reason), onRemove: () => choose({ reason: "" }), hs: "cand.filter.reason" } : null,
    filters.segment ? { name: P.segment, value: wordIn(segment.choices, filters.segment) ?? filters.segment, onRemove: () => choose({ segment: "" }), hs: "cand.filter.segment" } : null,
    filters.tags.trim() ? { name: P.tags, value: filters.tags.trim(), onRemove: () => choose({ tags: "" }), hs: "cand.filter.tags" } : null,
    filters.prior.trim() ? { name: P.priorRequisition, value: filters.prior.trim().toUpperCase(), onRemove: () => choose({ prior: "" }), hs: "cand.filter.prior" } : null,
    filters.lastContact ? { name: P.lastContact, value: filters.lastContact, onRemove: () => choose({ lastContact: "" }), hs: "cand.filter.last-contact" } : null,
  ];
  const active = chips.filter((chip): chip is FilterBarFilter => chip !== null);
  const refused = list.error ? refusalSentence(list.error) : null;

  return (
    <Stack gap={16}>
      <PageHeader
        title={NAV_ITEM_LABELS.candidates}
        actions={(
          <>
            {SavedViews ? <SavedViews query={viewQuery} apply={applyView} /> : null}
            <Button tone="blue" icon="plus" can={mayAdd} onClick={onAdd} >
              {DIALOG_LABELS.addCandidate}
            </Button>
          </>
        )}
      />
      <Flex gap={8} wrap>
        <FilterBar
          label={NAV_ITEM_LABELS.candidates}
          search={{ value: draft.q, onChange: (text) => typeIn("q", text), hs: "cand.search" }}
          filters={active}
          sort={{
            options: [{ value: "name", label: L.name }, { value: "last_contact", label: P.lastContact }],
            value: sort,
            onChange: (value) => {
              setSort(value === "last_contact" ? "last_contact" : "name");
              setPage(0);
            },
            hs: "cand.sort",
          }}
        />
      </Flex>
      <Disclosure sm title={L.filters} right={active.length > 0 ? <Count n={active.length} /> : undefined} >
        <Grid cols={5} min={160} gap={12}>
          <Field label={L.status}>
            <Select sm options={(["Active", "Archived", "Merged"] as const).map((value) => ({ value, label: STATUS_WORD[value] }))} value={draft.status} onChange={(value) => choose({ status: statusOf(value) })}  />
          </Field>
          <Field label={L.internalExternal}>
            <Select sm options={[{ value: "external", label: L.external }, { value: "internal", label: L.internal }]} value={draft.internal} onChange={(value) => choose({ internal: internalOf(value) })}  />
          </Field>
          <Field label={L.owner} can={mayPickOwner}>
            <Select sm options={owners.choices.map((choice) => ({ value: choice.value, label: choice.label }))} loading={owners.isLoading} value={draft.owner} onChange={(value) => choose({ owner: value })}  />
          </Field>
          <Field label={L.source}>
            <Select sm options={sources} value={draft.source} onChange={(value) => choose({ source: value })}  />
          </Field>
          <Field label={P.reasonDateAdded}>
            <Select sm options={REASONS_ADDED} value={draft.reason} onChange={(value) => choose({ reason: value })}  />
          </Field>
          <TaxonomySelect label={P.segment} taxonomy={segment} value={draft.segment} onChange={(value) => choose({ segment: value })} sm  />
          <Field label={P.tags}>
            <Input sm value={draft.tags} onChange={(event) => typeIn("tags", event.target.value)} autoComplete="off"  />
          </Field>
          <Field label={P.priorRequisition}>
            <Input sm mono value={draft.prior} onChange={(event) => typeIn("prior", event.target.value)} autoComplete="off"  />
          </Field>
          <Field label={P.lastContact}>
            <Input sm type="date" value={draft.lastContact} onChange={(event) => choose({ lastContact: event.target.value })}  />
          </Field>
          <Toggle label={L.talentCommunity} checked={draft.community} onChange={(value) => choose({ community: value })}  />
        </Grid>
      </Disclosure>
      {refused ? <Banner tone="red" live>{refused}</Banner> : null}
      {list.error && !refused ? (
        <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: list.error.status, refusal_code: list.error.code, request_id: list.error.requestId }} onRetry={() => void list.refetch()}  />
      ) : null}
      <Card flush>
        <Table
          dense
          stickyFirst
          label={NAV_ITEM_LABELS.candidates}
          loading={pending ? 5 : undefined}
          empty={list.data && rows.length === 0 ? "—" : undefined}
          cols={COLUMNS + extraColumns.length}
          head={(
            <Tr>
              <Th>{COMMON_LABELS.candidateId}</Th>
              <Th>{L.name}</Th>
              <Th>{BRD.currentEmployer}</Th>
              <Th>{L.source}</Th>
              <Th>{L.internalExternal}</Th>
              <Th align="r">{L.applications}</Th>
              <Th>{L.talentCommunity}</Th>
              <Th>{P.lastContact}</Th>
              <Th>{L.status}</Th>
              <Th>{L.owner}</Th>
              {extraColumns.map((column) => <Th key={column.key}>{column.header}</Th>)}
            </Tr>
          )}
        >
          {rows.map((row, index) => <Row key={row.ref} row={row} index={index} extra={extraColumns} />)}
        </Table>
      </Card>
      {list.data && total > 0 ? <Pager page={page} total={total} onPage={setPage} /> : null}
      {dialog}
    </Stack>
  );
}

/** The page stepper under the table: the rows shown over the total, and the pages before and after. */
function Pager({ page, total, onPage }: { page: number; total: number; onPage: (page: number) => void }) {
  return (
    <div role="group" aria-label={DOCUMENT_LABELS.pageStepper}>
      <Flex gap={6} justify="end">
        <IconButton sm icon="back" label={pageRange(page - 1, total)} onClick={() => onPage(page - 1)} can={page > 0}  />
        <span className="mc-t3" aria-live="polite">{pageRange(page, total)}</span>
        <IconButton sm icon="fwd" label={pageRange(page + 1, total)} onClick={() => onPage(page + 1)} can={(page + 1) * PAGE_SIZE < total}  />
      </Flex>
    </div>
  );
}

/** A value this seat does not see (the token projection, POLICY §3.1 P-CAND.hrbp): his masked words, never a blank. */
function Hidden() {
  return (
    <span className="mc-masked-w mc-t3">
      <Icon name="lock" size={14} />
      {MASKED_LABELS.subjectToPermission}
    </span>
  );
}

function Row({ row, index, extra }: { row: SearchRow; index: number; extra: readonly CandidateColumnDef[] }) {
  const sourceWord = useSourceWord();
  if (row.projection === "masked") return <MaskedRow cols={COLUMNS + extra.length} lead={[<span className="mc-mono" key="id">{row.candidate_no}</span>]} />;
  const id = <IdPill value={row.candidate_no}  />;
  const lastContact = row.community?.last_contact_at ? row.community.last_contact_at.slice(0, 10) : "—";
  if (!row.display_name) {
    // the token row (UAT F-21): the token label, opening the application this seat may read
    const label = row.candidate_id ? tokenLabel(row.candidate_id) : row.candidate_no;
    const href = openedApplication(row);
    return (
      <Tr >
        <Td label={COMMON_LABELS.candidateId} nowrap>{id}</Td>
        <Td label={L.name}>{href ? <TextLink href={href} >{label}</TextLink> : label}</Td>
        <Td label={BRD.currentEmployer}><Hidden /></Td>
        <Td label={L.source}><Hidden /></Td>
        <Td label={L.internalExternal}><Hidden /></Td>
        <Td label={L.applications} align="r"><Count n={row.applications?.length ?? 0} /></Td>
        <Td label={L.talentCommunity}>{row.community ? <Pill tone="info">{L.talentCommunity}</Pill> : "—"}</Td>
        <Td label={P.lastContact}>{lastContact}</Td>
        <Td label={L.status}><Pill>{STATUS_WORD[row.status]}</Pill></Td>
        <Td label={L.owner}><Hidden /></Td>
        {extra.map((column) => <Td key={column.key} label={column.header}>{column.cell(row)}</Td>)}
      </Tr>
    );
  }
  return (
    <Tr >
      <Td label={COMMON_LABELS.candidateId} nowrap>{id}</Td>
      <Td label={L.name}>
        {row.candidate_id ? <TextLink href={`#candidates/${row.candidate_id}`} >{row.display_name}</TextLink> : row.display_name}
      </Td>
      <Td label={BRD.currentEmployer}>{row.current_employer || "—"}</Td>
      <Td label={L.source}>{sourceWord(row.source_channel)}</Td>
      <Td label={L.internalExternal}>{row.internal === null || row.internal === undefined ? "—" : row.internal ? <Pill tone="violet">{L.internal}</Pill> : L.external}</Td>
      <Td label={L.applications} align="r"><Count n={row.applications?.length ?? 0} /></Td>
      <Td label={L.talentCommunity}>{row.community ? <Pill tone="info">{L.talentCommunity}</Pill> : "—"}</Td>
      <Td label={P.lastContact}>{lastContact}</Td>
      <Td label={L.status}><Pill>{STATUS_WORD[row.status]}</Pill></Td>
      <Td label={L.owner}>{row.owner_name ?? "—"}</Td>
      {extra.map((column) => <Td key={column.key} label={column.header}>{column.cell(row)}</Td>)}
    </Tr>
  );
}
